// src/pages/StickersPage.tsx
// QRシール管理画面（Web版）。モバイル版の3画面（最近登録した本/本棚から選択/過去に発行した
// シール）と同じ機能を1ページの View ステートマシンとしてまとめたもの。
//
// 実際の画像/PDF生成はKANRI_ID時代に作られた旧Lambdaにそのまま委譲しており、生成は
// 非同期処理（完了するとメールでPDFのダウンロードリンクが届く）。
//
// 図書館名の自動入力は廃止済みテーブル UserLibrarySettings に依存していたため非対応にし、
// 手入力＋前回入力値をブラウザに保存してヒントにする方式に簡略化している
// （モバイル版 sticker_confirm_page.dart と同じ方針）。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppTokens } from '../context/ThemeModeContext';
import { NEW_API } from '../config/api';
import { fetchShelvesRaw, ShelfDetail } from '../utils/shelfHandler';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import {
  generateStickers,
  listGeneratedStickers,
  regenerateDownloadUrl,
  GeneratedStickerHistoryItem,
} from '../utils/stickerApi';
import axios from 'axios';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import HistoryIcon from '@mui/icons-material/History';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';

// MUIの標準アイコンセットには「本棚（棚そのもの）」を表すアイコンが無い
// （CollectionsBookmarkは本の集まりに見え、個々の本のアイコンと紛らわしい）ので自作。
// 2段の棚板に本が並ぶ様子で表現する
const ShelvesIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props}>
    <path d="M4 3h16v2H4V3z" />
    <path d="M4 11h16v2H4v-2z" />
    <path d="M4 19h16v2H4v-2z" />
    <rect x="5" y="5.4" width="1.6" height="5.2" />
    <rect x="7.3" y="5.4" width="1.6" height="5.2" />
    <rect x="9.6" y="5.4" width="1.6" height="5.2" />
    <rect x="13" y="5.4" width="1.6" height="5.2" />
    <rect x="15.3" y="5.4" width="1.6" height="5.2" />
    <rect x="6" y="13.4" width="1.6" height="5.2" />
    <rect x="8.3" y="13.4" width="1.6" height="5.2" />
    <rect x="12" y="13.4" width="1.6" height="5.2" />
    <rect x="14.3" y="13.4" width="1.6" height="5.2" />
    <rect x="16.6" y="13.4" width="1.6" height="5.2" />
  </SvgIcon>
);

type View = 'home' | 'selectRecent' | 'selectByShelf' | 'confirm' | 'ordered' | 'history';

interface StickerBook {
  global_book_id: string;
  rl_book_id: number;
  rl_global_shelf_id: string | null;
  title: string;
  author: string;
  isbn13: string | null;
  registered_at: string | null;
}

const LAST_LIBRARY_NAME_KEY_PREFIX = 'lastStickerLibraryName_';
const LAST_LIBRARY_COLOR_KEY_PREFIX = 'lastStickerLibraryColor_';

// 図書館名の背景色プリセット。値がnullなら従来通り背景無し・黒文字のまま
const LIBRARY_NAME_COLOR_PRESETS: { label: string; value: string | null }[] = [
  { label: 'なし', value: null },
  { label: 'レッド', value: '#e53935' },
  { label: 'オレンジ', value: '#fb8c00' },
  { label: 'イエロー', value: '#fdd835' },
  { label: 'グリーン', value: '#43a047' },
  { label: 'ブルー', value: '#1e88e5' },
  { label: 'パープル', value: '#8e24aa' },
  { label: 'ネイビー', value: '#233048' },
];

// 図書館名の文字数制限（全角15文字まで、半角文字は0.5文字としてカウント）。
// モバイル版(sticker_confirm_page.dart)と同じ計算方法を使う
const MAX_LIBRARY_NAME_WEIGHT = 15;

const isHalfWidthChar = (char: string): boolean => {
  const code = char.codePointAt(0) ?? 0;
  // 半角英数字・記号（ASCII）
  if (code >= 0x0020 && code <= 0x007e) return true;
  // 半角カタカナ
  if (code >= 0xff61 && code <= 0xff9f) return true;
  return false;
};

const calculateLibraryNameWeight = (text: string): number => {
  let total = 0;
  for (const char of Array.from(text)) {
    total += isHalfWidthChar(char) ? 0.5 : 1;
  }
  return total;
};

const StickersPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { idToken, isLoggedIn, user, userSub } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState<View>('home');
  const [loading, setLoading] = useState(false);

  const [books, setBooks] = useState<StickerBook[]>([]);
  const [shelves, setShelves] = useState<ShelfDetail[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [shelfSearchText, setShelfSearchText] = useState('');

  // 本棚からシール作成画面: 棚一覧（冊数のみ）は先に読み込み、各棚の中身は
  // 展開したときに初めて取得する（読み込み済みの棚はキャッシュを使い回す）
  const [shelfBooksCache, setShelfBooksCache] = useState<Record<string, StickerBook[]>>({});
  const [loadingShelfKeys, setLoadingShelfKeys] = useState<Set<string>>(new Set());

  const [recentNextCursor, setRecentNextCursor] = useState<string | null>(null);
  const [loadingMoreRecent, setLoadingMoreRecent] = useState(false);

  const [emailInput, setEmailInput] = useState('');
  const [libraryNameInput, setLibraryNameInput] = useState('');
  const libraryNameTooLong = useMemo(
    () => calculateLibraryNameWeight(libraryNameInput) > MAX_LIBRARY_NAME_WEIGHT,
    [libraryNameInput]
  );
  const [libraryNameBgColorInput, setLibraryNameBgColorInput] = useState<string | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [includeClassification, setIncludeClassification] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderedEmail, setOrderedEmail] = useState('');

  const [history, setHistory] = useState<GeneratedStickerHistoryItem[] | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<number | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (isLoggedIn) return;
    const t = window.setTimeout(() => navigate('/', { replace: true }), 1200);
    return () => window.clearTimeout(t);
  }, [isLoggedIn, navigate]);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchBooksScoped = useCallback(
    async (opts?: { shelfId?: string; noShelfOnly?: boolean }): Promise<StickerBook[]> => {
      if (!idToken) return [];
      const headers = { Authorization: `Bearer ${idToken}` };
      const all: StickerBook[] = [];
      let cursor: string | null = null;
      do {
        const params: Record<string, string | number> = { limit: 100 };
        if (cursor) params.cursor = cursor;
        if (opts?.shelfId) params.shelf_id = opts.shelfId;
        if (opts?.noShelfOnly) params.no_shelf_only = 'true';
        const res = await axios.get(NEW_API.BOOKS, { headers, params });
        const data = res.data?.data ?? {};
        const items: any[] = data.items ?? [];
        items.forEach((item) => {
          all.push({
            global_book_id: item.global_book_id,
            rl_book_id: item.rl_book_id,
            rl_global_shelf_id: item.rl_global_shelf_id ?? null,
            title: item.title ?? '(タイトル未登録)',
            author: item.author ?? '',
            isbn13: item.isbn13 ?? null,
            registered_at: item.global_book_registered_at ?? null,
          });
        });
        cursor = data.next_cursor ?? null;
      } while (cursor);
      return all;
    },
    [idToken],
  );

  const loadAllBooks = useCallback(() => fetchBooksScoped(), [fetchBooksScoped]);

  const RECENT_PAGE_SIZE = 65;

  // 登録日時が新しい順に1ページ分（シール1枚分＝65冊）だけ取得する。
  // 「最近登録した本のシールを作る」画面向け。全件取得する loadAllBooks とは異なり、
  // 蔵書数が多いユーザーでも高速に開ける（2026-08-25、全件一括取得だった不具合を修正）
  const fetchRecentBooksPage = useCallback(
    async (cursor?: string | null): Promise<{ items: StickerBook[]; nextCursor: string | null }> => {
      if (!idToken) return { items: [], nextCursor: null };
      const headers = { Authorization: `Bearer ${idToken}` };
      const params: Record<string, string | number> = { limit: RECENT_PAGE_SIZE, sort: 'recent' };
      if (cursor) params.cursor = cursor;
      const res = await axios.get(NEW_API.BOOKS, { headers, params });
      const data = res.data?.data ?? {};
      const items: StickerBook[] = (data.items ?? []).map((item: any) => ({
        global_book_id: item.global_book_id,
        rl_book_id: item.rl_book_id,
        rl_global_shelf_id: item.rl_global_shelf_id ?? null,
        title: item.title ?? '(タイトル未登録)',
        author: item.author ?? '',
        isbn13: item.isbn13 ?? null,
        registered_at: item.global_book_registered_at ?? null,
      }));
      return { items, nextCursor: data.next_cursor ?? null };
    },
    [idToken],
  );

  const UNSHELVED_KEY = '__none__';

  const loadShelfGroup = useCallback(
    async (key: string, opts?: { shelfId?: string; noShelfOnly?: boolean }) => {
      if (shelfBooksCache[key] || loadingShelfKeys.has(key)) return;
      setLoadingShelfKeys((prev) => new Set(prev).add(key));
      try {
        const items = await fetchBooksScoped(opts);
        setShelfBooksCache((prev) => ({ ...prev, [key]: items }));
      } finally {
        setLoadingShelfKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [fetchBooksScoped, shelfBooksCache, loadingShelfKeys],
  );

  const openSelectRecent = async () => {
    setLoading(true);
    try {
      const page = await fetchRecentBooksPage();
      setBooks(page.items);
      setRecentNextCursor(page.nextCursor);
      setSelectedIds(new Set(page.items.map((b) => b.global_book_id)));
      setView('selectRecent');
    } catch (e) {
      showSnackbar('本の一覧の取得に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreRecent = async () => {
    if (loadingMoreRecent || !recentNextCursor) return;
    setLoadingMoreRecent(true);
    try {
      const page = await fetchRecentBooksPage(recentNextCursor);
      setBooks((prev) => [...prev, ...page.items]);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        page.items.forEach((b) => next.add(b.global_book_id));
        return next;
      });
      setRecentNextCursor(page.nextCursor);
    } catch (e) {
      showSnackbar('本の追加取得に失敗しました', 'error');
    } finally {
      setLoadingMoreRecent(false);
    }
  };

  const openSelectByShelf = async () => {
    setLoading(true);
    try {
      const shelfItems = idToken ? await fetchShelvesRaw(idToken) : [];
      shelfItems.sort((a, b) => (a.global_shelf_order ?? 0) - (b.global_shelf_order ?? 0));
      setShelves(shelfItems);
      setShelfBooksCache({});
      setLoadingShelfKeys(new Set());
      setSelectedIds(new Set());
      setShelfSearchText('');
      setView('selectByShelf');
    } catch (e) {
      showSnackbar('本棚一覧の取得に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async () => {
    setLoading(true);
    setView('history');
    try {
      if (!idToken) return;
      const items = await listGeneratedStickers(idToken);
      setHistory(items);
    } catch (e) {
      showSnackbar('履歴の取得に失敗しました', 'error');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = () => {
    const lastLibraryName = userSub ? window.localStorage.getItem(`${LAST_LIBRARY_NAME_KEY_PREFIX}${userSub}`) : null;
    const lastLibraryColor = userSub ? window.localStorage.getItem(`${LAST_LIBRARY_COLOR_KEY_PREFIX}${userSub}`) : null;
    setEmailInput(user?.email ?? '');
    setLibraryNameInput(lastLibraryName ?? '');
    setLibraryNameBgColorInput(lastLibraryColor ?? null);
    setNoteInput(`${selectedIds.size}冊のシール`);
    setIncludeClassification(false);
    setView('confirm');
  };

  const toggleSelected = (globalBookId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(globalBookId)) next.delete(globalBookId);
      else next.add(globalBookId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!idToken || selectedIds.size === 0) return;
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) {
      showSnackbar('メールアドレスを入力してください', 'error');
      return;
    }
    setSubmitting(true);
    try {
      if (userSub) {
        window.localStorage.setItem(`${LAST_LIBRARY_NAME_KEY_PREFIX}${userSub}`, libraryNameInput.trim());
        window.localStorage.setItem(`${LAST_LIBRARY_COLOR_KEY_PREFIX}${userSub}`, libraryNameBgColorInput ?? '');
      }
      const result = await generateStickers(
        Array.from(selectedIds),
        libraryNameInput.trim(),
        noteInput.trim(),
        idToken,
        includeClassification,
        libraryNameInput.trim() ? libraryNameBgColorInput : null,
      );
      setOrderedEmail(result.email || trimmedEmail);
      setView('ordered');
    } catch (e: any) {
      showSnackbar(e?.response?.data?.error?.message || 'シール作成の依頼に失敗しました', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyDownloadLink = async (generatedId: number) => {
    if (!idToken) return;
    setRegeneratingId(generatedId);
    try {
      const url = await regenerateDownloadUrl(generatedId, idToken);
      await navigator.clipboard.writeText(url);
      showSnackbar('ダウンロードリンクをコピーしました', 'success');
    } catch (e) {
      showSnackbar('リンクの取得に失敗しました', 'error');
    } finally {
      setRegeneratingId(null);
    }
  };

  const resetToHome = () => {
    setSelectedIds(new Set());
    setBooks([]);
    setShelves([]);
    setShelfBooksCache({});
    setLoadingShelfKeys(new Set());
    setRecentNextCursor(null);
    setLoadingMoreRecent(false);
    setView('home');
  };

  const filterBySearch = useCallback(
    (list: StickerBook[]) => {
      if (!shelfSearchText.trim()) return list;
      const q = shelfSearchText.toLowerCase();
      return list.filter(
        (b) => b.title.toLowerCase().includes(q) || b.author.toLowerCase().includes(q) || (b.isbn13 ?? '').toLowerCase().includes(q),
      );
    },
    [shelfSearchText],
  );

  const cardSx = {
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.glass,
    borderRadius: '18px',
    p: 2.5,
  } as const;

  const primaryButtonSx = {
    borderRadius: '999px',
    textTransform: 'none',
    fontWeight: 700,
    py: 1,
    color: TOKENS.bg,
    background: TOKENS.text,
    '&:hover': { background: TOKENS.textSub },
    '&.Mui-disabled': { color: TOKENS.bg, background: TOKENS.borderSoft },
  } as const;

  const inputSx = {
    '& .MuiOutlinedInput-root': { color: TOKENS.text, background: TOKENS.buttonBg, borderRadius: '12px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent },
    '& .MuiInputLabel-root': { color: TOKENS.textSub },
    '& .MuiInputLabel-root.Mui-focused': { color: TOKENS.text },
  } as const;

  const bookRow = (book: StickerBook) => (
    <Box
      key={book.global_book_id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        border: `1px solid ${TOKENS.border}`,
        background: TOKENS.glass,
        borderRadius: '14px',
        p: 1.25,
      }}
    >
      <Checkbox checked={selectedIds.has(book.global_book_id)} onChange={() => toggleSelected(book.global_book_id)} />
      <Box
        component="img"
        src={generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13)}
        alt={book.title}
        sx={{ width: 40, height: 56, objectFit: 'contain', borderRadius: '4px', background: TOKENS.cardBg, flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography sx={{ fontSize: 13, color: TOKENS.text }} noWrap>
          {book.title}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }} noWrap>
          {book.author}
        </Typography>
      </Box>
    </Box>
  );

  // シールの作成方法案内。以前は「印刷用ラベルシールのご案内」として作成完了直後と
  // 履歴画面に出していたが、2026-08-26にシール管理トップへ移設のうえ、内容をモバイル版
  // 「ReadLinker QR(シール)について」(about_sticker_page.dart)と同じ3ステップの
  // 作成方法案内に差し替えた（カードのデザイン自体はWeb既存のものを踏襲）
  const stickerPrintLinks = [
    { label: 'ヨドバシカメラの販売サイト', url: 'https://www.yodobashi.com/product/100000001000684729/' },
    { label: 'ビックカメラの販売サイト', url: 'https://www.biccamera.com/bc/item/1374437/' },
    { label: 'Amazonの販売サイト(販売数量を確認してください)', url: 'https://www.amazon.co.jp/dp/B08LPJJ472?ref_=cm_sw_r_cp_ud_dp_96PWVQQZHBYVWWGDBD9K' },
    { label: 'エーワン公式サイト', url: 'https://www.a-one.co.jp/product/search/detail.php?id=31269' },
  ];

  const stickerHowToStep = (number: string, title: string, body: React.ReactNode) => (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 22,
          height: 22,
          borderRadius: '999px',
          background: TOKENS.text,
          color: TOKENS.bg,
          fontSize: 12,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {number}
      </Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.text }}>{title}</Typography>
        <Box sx={{ mt: 0.5 }}>{body}</Box>
      </Box>
    </Box>
  );

  const stickerHowToSection = (
    <Box sx={{ ...cardSx, background: TOKENS.glassStrong }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <LocalPrintshopIcon sx={{ color: TOKENS.accent, fontSize: 20 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: TOKENS.text }}>
          シールの作成方法
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {stickerHowToStep(
          '1',
          'PDFファイルをダウンロードします。',
          <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
            シールの作成が完了すると、登録されているメールアドレスへDLリンクが送付されます。PDFファイルは複数ページになる場合があります。
          </Typography>,
        )}
        {stickerHowToStep(
          '2',
          '印刷します。',
          <Box>
            <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
              シールの印刷用紙はエーワン製 A-one 31269 をお使いください。
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              {stickerPrintLinks.map((item) => (
                <Box
                  key={item.label}
                  component="a"
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 12.5,
                    color: TOKENS.text,
                    textDecoration: 'underline',
                    textDecorationColor: TOKENS.accent,
                    '&:hover': { color: TOKENS.accent },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mt: 1 }}>
              家庭用プリンターでも印刷可能です。印刷する際には用紙サイズはA4、倍率を「100%」に設定してください。
            </Typography>
          </Box>,
        )}
        {stickerHowToStep(
          '3',
          '本に貼ります。',
          <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
            本の裏側などに貼っていただくのが使いやすいです。
          </Typography>,
        )}
        {stickerHowToStep(
          '4',
          '剥がれないようにするには、市販のラベルキーパーをご利用ください。',
          null,
        )}
      </Box>
    </Box>
  );

  const bottomBar = (children: React.ReactNode) => (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: TOKENS.glassStrong,
        borderTop: `1px solid ${TOKENS.border}`,
        p: 2,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 720 }}>{children}</Box>
    </Box>
  );

  return (
    <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg, pb: 10 }}>
      <Box sx={{ maxWidth: 720, margin: 'auto', px: 2, py: { xs: 3, md: 4 } }}>
        <Button component={Link} to="/bibliography" sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}>
          ← 書誌管理
        </Button>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: TOKENS.text, mb: 0.5 }}>シール管理（ReadLinker QR）</Typography>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 3 }}>
          登録した本のQRコード付きシールをPDFで作成します
        </Typography>

        {view === 'home' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MenuBookIcon sx={{ color: TOKENS.textSub, fontSize: 20 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>最近登録した本のシールを作る</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1.5 }}>
                登録日時が新しい順に65冊（シール1枚分）ずつ表示し、読み込んだ分をあらかじめ選択しておきます
              </Typography>
              <Button fullWidth disabled={loading} onClick={openSelectRecent} sx={primaryButtonSx}>
                {loading ? <CircularProgress size={18} sx={{ color: TOKENS.bg }} /> : '本を選ぶ'}
              </Button>
            </Box>

            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ShelvesIcon sx={{ color: TOKENS.textSub, fontSize: 20 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>本棚から本を選択してシールを作る</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1.5 }}>
                本棚ごとにまとめて選んだり、検索で絞り込んだりできます
              </Typography>
              <Button fullWidth disabled={loading} onClick={openSelectByShelf} sx={primaryButtonSx}>
                {loading ? <CircularProgress size={18} sx={{ color: TOKENS.bg }} /> : '本棚から選ぶ'}
              </Button>
            </Box>

            <Box sx={cardSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HistoryIcon sx={{ color: TOKENS.textSub, fontSize: 20 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text }}>過去に発行したシール</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1.5 }}>
                過去に作成したシールPDFのダウンロードリンクを再発行できます
              </Typography>
              <Button
                fullWidth
                onClick={openHistory}
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: 13,
                  fontWeight: 700,
                  py: 1,
                  color: TOKENS.text,
                  background: TOKENS.buttonBg,
                  border: `1px solid ${TOKENS.borderSoft}`,
                  '&:hover': { borderColor: TOKENS.accent, background: TOKENS.buttonBgHover },
                }}
              >
                履歴を見る
              </Button>
            </Box>

            {stickerHowToSection}
          </Box>
        )}

        {view === 'selectRecent' && (
          <Box>
            <Button onClick={resetToHome} sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}>
              ← 戻る
            </Button>
            {books.length === 0 ? (
              <Typography sx={{ color: TOKENS.textSub, fontSize: 13, textAlign: 'center', mt: 4 }}>
                登録されている本がありません
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pb: 2 }}>{books.map(bookRow)}</Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', pb: 10 }}>
              {recentNextCursor ? (
                <Button
                  disabled={loadingMoreRecent}
                  onClick={loadMoreRecent}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    fontSize: 13,
                    fontWeight: 700,
                    px: 2.5,
                    py: 0.75,
                    color: TOKENS.text,
                    background: TOKENS.buttonBg,
                    border: `1px solid ${TOKENS.borderSoft}`,
                    '&:hover': { borderColor: TOKENS.accent, background: TOKENS.buttonBgHover },
                  }}
                >
                  {loadingMoreRecent ? <CircularProgress size={16} sx={{ color: TOKENS.text }} /> : `次の${RECENT_PAGE_SIZE}冊を読み込む`}
                </Button>
              ) : books.length > 0 ? (
                <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>これ以上の本はありません</Typography>
              ) : null}
            </Box>
            {bottomBar(
              <Button fullWidth disabled={selectedIds.size === 0} onClick={openConfirm} sx={{ ...primaryButtonSx, borderRadius: '10px', py: 1.1 }}>
                選択した{selectedIds.size}冊のシールを作る
              </Button>,
            )}
          </Box>
        )}

        {view === 'selectByShelf' && (
          <Box>
            <Button onClick={resetToHome} sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}>
              ← 戻る
            </Button>
            <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1.5 }}>
              棚をタップすると中身を読み込みます。検索は開いた棚の中だけが対象です。
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="タイトル・著者・ISBNで検索（開いた棚の中のみ）"
              value={shelfSearchText}
              onChange={(e) => setShelfSearchText(e.target.value)}
              sx={{ ...inputSx, mb: 2 }}
            />

            {[
              ...shelves.map((s) => ({
                key: s.rl_global_shelf_id,
                name: s.global_shelf_name,
                countHint: s.book_count,
                opts: { shelfId: s.rl_global_shelf_id },
              })),
              { key: UNSHELVED_KEY, name: '本棚未分類の本', countHint: undefined, opts: { noShelfOnly: true } },
            ].map((group) => {
              const loadedBooks = shelfBooksCache[group.key];
              const isLoadingGroup = loadingShelfKeys.has(group.key);
              const visibleBooks = loadedBooks ? filterBySearch(loadedBooks) : undefined;
              const groupIds = loadedBooks ? loadedBooks.map((b) => b.global_book_id) : [];
              const isAllSelected = groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));
              const countLabel = loadedBooks ? `${loadedBooks.length}冊` : group.countHint != null ? `${group.countHint}冊` : '';

              return (
                <Accordion
                  key={group.key}
                  disableGutters
                  onChange={(_, expanded) => {
                    if (expanded) loadShelfGroup(group.key, group.opts);
                  }}
                  sx={{
                    mb: 1,
                    background: TOKENS.glass,
                    border: `1px solid ${TOKENS.border}`,
                    borderRadius: '14px !important',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: TOKENS.textSub }} />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {loadedBooks && (
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={!isAllSelected && groupIds.some((id) => selectedIds.has(id))}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) groupIds.forEach((id) => next.add(id));
                              else groupIds.forEach((id) => next.delete(id));
                              return next;
                            });
                          }}
                        />
                      )}
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: TOKENS.text }}>
                        {group.name}（{countLabel}）
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {isLoadingGroup ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} sx={{ color: TOKENS.text }} />
                      </Box>
                    ) : visibleBooks && visibleBooks.length === 0 ? (
                      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, textAlign: 'center', py: 1 }}>
                        {shelfSearchText ? '検索条件に一致する本がありません' : 'この棚には本がありません'}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{visibleBooks?.map(bookRow)}</Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}

            <Box sx={{ height: 80 }} />
            {bottomBar(
              <Button fullWidth disabled={selectedIds.size === 0} onClick={openConfirm} sx={{ ...primaryButtonSx, borderRadius: '10px', py: 1.1 }}>
                選択した{selectedIds.size}冊のシールを作る
              </Button>,
            )}
          </Box>
        )}

        {view === 'confirm' && (
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text, mb: selectedIds.size > 6000 ? 0.5 : 2 }}>
              シール作成の確認（{selectedIds.size}冊）
            </Typography>
            {selectedIds.size > 6000 && (
              <Typography sx={{ fontSize: 12.5, color: 'error.main', mb: 2 }}>
                一度に指定できるのは6000冊までです。選択数を減らしてください。
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              label="作成したシールのPDFファイルを送信するメールアドレス"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              sx={{ ...inputSx, mb: 2.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label="図書館名（任意）"
              value={libraryNameInput}
              onChange={(e) => setLibraryNameInput(e.target.value)}
              sx={{ ...inputSx, mb: libraryNameTooLong ? 0.5 : 2.5 }}
            />
            {libraryNameTooLong && (
              <Typography sx={{ fontSize: 12, color: '#e65100', mb: 2, fontWeight: 700 }}>
                図書館名は全角15文字相当まで入力できます
              </Typography>
            )}
            {libraryNameInput.trim() && (
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1 }}>
                  図書館名の背景色（シール上部に帯として表示されます）
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {LIBRARY_NAME_COLOR_PRESETS.map((preset) => {
                    const isSelected = libraryNameBgColorInput === preset.value;
                    return (
                      <Box
                        key={preset.label}
                        onClick={() => setLibraryNameBgColorInput(preset.value)}
                        title={preset.label}
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '999px',
                          cursor: 'pointer',
                          background: preset.value ?? TOKENS.buttonBg,
                          border: isSelected ? `2px solid ${TOKENS.accent}` : `1px solid ${TOKENS.borderSoft}`,
                          boxShadow: isSelected ? `0 0 0 2px ${TOKENS.bg}, 0 0 0 3px ${TOKENS.accent}` : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {!preset.value && (
                          <Typography sx={{ fontSize: 9, color: TOKENS.textSub }}>無</Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
            <TextField
              fullWidth
              size="small"
              label="ファイルについての一言メモ"
              multiline
              minRows={2}
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              sx={{ ...inputSx, mb: 1 }}
            />
            <FormControlLabel
              sx={{ mb: 2.5, ml: 0 }}
              control={
                <Switch
                  checked={includeClassification}
                  onChange={(e) => setIncludeClassification(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: 13.5, color: TOKENS.text }}>NDC(日本十進分類表)番号を付与</Typography>
                  <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }}>
                    シールに分類番号を印字します（分類が未登録の本は空欄になります）
                  </Typography>
                </Box>
              }
            />
            <Button
              fullWidth
              disabled={submitting || selectedIds.size > 6000 || libraryNameTooLong}
              onClick={handleSubmit}
              sx={primaryButtonSx}
            >
              {submitting ? <CircularProgress size={18} sx={{ color: TOKENS.bg }} /> : 'シールを作る'}
            </Button>
          </Box>
        )}

        {view === 'ordered' && (
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <MarkEmailReadIcon sx={{ color: '#2e7d32', fontSize: 22 }} />
              <Typography sx={{ fontSize: 15, color: TOKENS.text }}>シールを作成中です</Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: TOKENS.textSub, mb: 2 }}>
              完了後、以下のメールアドレスへPDFデータのダウンロードリンクが届きます（リンクは発行から24時間有効です。期限切れの場合はこの画面の「過去に発行したシール」から再発行できます）。
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: TOKENS.text, mb: 2.5 }}>{orderedEmail}</Typography>

            <Button
              fullWidth
              onClick={resetToHome}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 700,
                color: TOKENS.text,
                background: TOKENS.buttonBg,
                border: `1px solid ${TOKENS.borderSoft}`,
                '&:hover': { borderColor: TOKENS.accent, background: TOKENS.buttonBgHover },
              }}
            >
              シール管理に戻る
            </Button>
          </Box>
        )}

        {view === 'history' && (
          <Box>
            <Button onClick={resetToHome} sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}>
              ← 戻る
            </Button>

            {loading || history === null ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={24} sx={{ color: TOKENS.text }} />
              </Box>
            ) : history.length === 0 ? (
              <Typography sx={{ color: TOKENS.textSub, fontSize: 13, textAlign: 'center', mt: 4 }}>
                まだシールを作成していません
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {history.map((item) => (
                  <Box
                    key={item.generated_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      border: `1px solid ${TOKENS.border}`,
                      background: TOKENS.glass,
                      borderRadius: '14px',
                      p: 1.5,
                    }}
                  >
                    <PictureAsPdfIcon sx={{ color: TOKENS.textSub, fontSize: 22 }} />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography sx={{ fontSize: 13, color: TOKENS.text }} noWrap>
                        {item.note || '(メモなし)'}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }} noWrap>
                        {item.generated_at ? new Date(item.generated_at.replace(' ', 'T')).toLocaleString('ja-JP') : ''}
                      </Typography>
                    </Box>
                    <Button
                      disabled={regeneratingId === item.generated_id}
                      onClick={() => handleCopyDownloadLink(item.generated_id)}
                      sx={{
                        flexShrink: 0,
                        borderRadius: '999px',
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 700,
                        px: 1.5,
                        color: TOKENS.bg,
                        background: TOKENS.text,
                        '&:hover': { background: TOKENS.textSub },
                      }}
                    >
                      {regeneratingId === item.generated_id ? (
                        <CircularProgress size={14} sx={{ color: TOKENS.bg }} />
                      ) : (
                        'DLリンクをコピー'
                      )}
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default StickersPage;
